import { useEffect, useMemo, useState } from "react";

import { GrowthUnifiedProfile } from "@/libraries/growth-unified-profile-client";
import { useGupsStatus } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks";

import { useUserContext } from "../../../../components/user-context";
import { useLoggedInUser } from "../../../../utils";
import { useGeoLocation } from "../../../../utils/useGeoCurrency";
import { useWAEContext } from "../../../utils/wae-context-provider";

import {
    SoftwarePageProductRecommendation,
    softwareRecommendation
} from "./software-page-product-recommendation";

export enum UserSegmentTypes {
    LOGIN_STATUS = "login-status",
    GEOLOCATION = "geolocation",
    JOB_FUNCTION = "job-function",
    DOMAIN_TYPE = "domain-type",
    OWNED_PRODUCTS = "owned-products",
    CLUSTER_TAG = "cluster-tag",
    RECOMMENDED_PRODUCTS = "recommended-products"
}

export interface UserSegmentResponse {
    data?: UserSegmentValues;
    error?: unknown;
    status: "idle" | "fetching" | "complete";
}

export interface UserSegmentValues {
    [UserSegmentTypes.LOGIN_STATUS]?: "loggedIn" | "loggedOut";
    [UserSegmentTypes.GEOLOCATION]?: string;
    [UserSegmentTypes.JOB_FUNCTION]?: string;
    [UserSegmentTypes.DOMAIN_TYPE]?: string;
    [UserSegmentTypes.OWNED_PRODUCTS]?: string[];
    [UserSegmentTypes.CLUSTER_TAG]?: string;
    [UserSegmentTypes.RECOMMENDED_PRODUCTS]?: softwareRecommendation[];
}

// temporary implementation for testing purposes
export const useUserSegments = (): UserSegmentResponse => {
    const [status, setStatus] = useState<"idle" | "fetching" | "complete">(
        "idle"
    );
    const [profile, setProfile] = useState<GrowthUnifiedProfile>();
    const [profileError, setProfileError] = useState<unknown>(null);
    const loggedInUser = useLoggedInUser();
    const { personalizationValues } = useWAEContext();
    const { loggedOutProfile } = useUserContext();
    // Memoize the client to prevent recreation on every render
    const { getFullProfileCallback } = useGupsStatus();
    const envSource = typeof window !== "undefined" ? window : process.env;
    const { countryCode, status: locationStatus } = useGeoLocation(
        envSource["PRODUCT_CATALOG_LOCATION_BASE_URL"] as string
    );
    useEffect(() => {
        const getFullProfile = async () => {
            setStatus("fetching");
            const { data, error } = await getFullProfileCallback();
            setProfileError(error);
            setProfile(data);
            setStatus("complete");
        };

        getFullProfile();
    }, [getFullProfileCallback]);

    // Helper function to extract product names from sites
    const extractProductNames = (
        sites?: Array<{
            products?: Array<{ productName?: string; productKey?: string }>;
        }>
    ) => {
        if (!sites) return [];

        return sites.flatMap(
            (site) =>
                site.products
                    ?.map(
                        (product) =>
                            product.productName || product.productKey || ""
                    )
                    .filter(Boolean) || []
        );
    };

    // Memoize the personalization values to prevent unnecessary recalculations
    return useMemo(() => {
        // Collect products from both logged-in and logged-out profiles
        const loggedInProducts = extractProductNames(profile?.sites);
        const loggedOutProducts = extractProductNames(loggedOutProfile?.sites);
        const allProducts = [...loggedInProducts, ...loggedOutProducts];
        const uniqueProducts = [...new Set(allProducts)];

        // Build the user segment values
        const userSegmentData: UserSegmentValues = {
            ...personalizationValues,
            [UserSegmentTypes.LOGIN_STATUS]: loggedInUser.isUserLoggedIn
                ? "loggedIn"
                : "loggedOut",
            [UserSegmentTypes.GEOLOCATION]:
                locationStatus === "complete" ? countryCode : undefined,
            [UserSegmentTypes.JOB_FUNCTION]: profile?.userProfile?.jobFunction,
            [UserSegmentTypes.DOMAIN_TYPE]: profile?.userProfile?.domainType,
            [UserSegmentTypes.OWNED_PRODUCTS]: uniqueProducts,
            [UserSegmentTypes.RECOMMENDED_PRODUCTS]:
                SoftwarePageProductRecommendation({
                    jobFunction: profile?.userProfile?.jobFunction,
                    loggedInProducts: loggedInProducts
                })
        };

        return {
            data: userSegmentData,
            error: profileError,
            status
        };
    }, [
        loggedInUser.isUserLoggedIn,
        profile?.sites,
        profile?.userProfile?.domainType,
        profile?.userProfile?.jobFunction,
        loggedOutProfile?.sites,
        profileError,
        countryCode,
        status,
        locationStatus,
        personalizationValues
    ]);
};
